<script>
import { ref, provide, useTemplateRef } from 'vue';
import { useElementSize } from '@vueuse/core';
// composable
import { useLabelSuggestions } from 'dashboard/composables/useLabelSuggestions';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// components
import ReplyBox from './ReplyBox.vue';
import MessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ResizableEditorWrapper from './ResizableEditorWrapper.vue';

// stores and apis
import { mapGetters } from 'vuex';
import MessageApi from 'dashboard/api/inbox/message';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { getTypingUsersText } from '../../../helper/commons';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

const OMNICHANNEL_TYPES = new Set([INBOX_TYPES.TELEGRAM, INBOX_TYPES.WAZZUP]);
const MESSAGE_PAGE_SIZE = 20;
const MAX_HISTORY_PAGES = 50;

export default {
  components: {
    MessageList,
    ReplyBox,
    Banner,
    ConversationLabelSuggestion,
    Spinner,
    ResizableEditorWrapper,
  },
  mixins: [inboxMixin],
  setup() {
    const conversationPanelRef = ref(null);
    const resizableEditorWrapperRef = ref(null);
    const messagesViewRef = useTemplateRef('messagesViewRef');
    const topBannerRef = useTemplateRef('topBannerRef');
    const { height: containerHeight } = useElementSize(messagesViewRef);
    const { height: topBannerHeight } = useElementSize(topBannerRef);

    const {
      captainTasksEnabled,
      isLabelSuggestionFeatureEnabled,
      getLabelSuggestions,
    } = useLabelSuggestions();

    provide('contextMenuElementTarget', conversationPanelRef);

    return {
      captainTasksEnabled,
      getLabelSuggestions,
      isLabelSuggestionFeatureEnabled,
      conversationPanelRef,
      resizableEditorWrapperRef,
      messagesViewRef,
      topBannerRef,
      containerHeight,
      topBannerHeight,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      relatedMessagesByConversation: {},
      isLoadingRelatedMessages: false,
      omnichannelLoadToken: 0,
      replyConversationId: null,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
    }),
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    contactId() {
      return this.currentChat?.meta?.sender?.id;
    },
    contactConversations() {
      if (!this.contactId) return [];

      return this.$store.getters['contactConversations/getContactConversation'](
        this.contactId
      );
    },
    omnichannelConversations() {
      if (this.currentChat?.unified_chat) return [];
      return this.contactConversations.filter(conversation => {
        const inbox = this.$store.getters['inboxes/getInbox'](
          conversation.inbox_id
        );
        return OMNICHANNEL_TYPES.has(inbox?.channel_type);
      });
    },
    replyChannels() {
      if (this.currentChat?.unified_chat) {
        return (this.currentChat.reply_channels || [])
          .filter(channel => channel.can_reply)
          .map(channel => ({
            ...channel,
            inboxId: channel.inbox_id,
            icon:
              {
                [INBOX_TYPES.TELEGRAM]: 'i-ri-telegram-fill',
                [INBOX_TYPES.WAZZUP]: 'i-ri-whatsapp-fill',
              }[channel.channel_type] || 'i-lucide-message-square',
          }));
      }
      const conversationsByInbox = new Map();
      this.omnichannelConversations
        .filter(conversation => conversation.can_reply)
        .sort((left, right) => {
          const openPriority =
            Number(right.status === 'open') - Number(left.status === 'open');
          return (
            openPriority ||
            right.last_activity_at - left.last_activity_at ||
            right.id - left.id
          );
        })
        .forEach(conversation => {
          if (!conversationsByInbox.has(conversation.inbox_id)) {
            conversationsByInbox.set(conversation.inbox_id, conversation);
          }
        });

      const channels = [...conversationsByInbox.values()].map(conversation => {
        const inbox = this.$store.getters['inboxes/getInbox'](
          conversation.inbox_id
        );
        const channelType = inbox?.channel_type;
        let label = inbox?.name;
        if (channelType === INBOX_TYPES.WAZZUP) label = 'WhatsApp';
        if (channelType === INBOX_TYPES.TELEGRAM) label = 'Telegram';

        return {
          id: conversation.id,
          inboxId: conversation.inbox_id,
          conversationIds: this.omnichannelConversations
            .filter(item => item.inbox_id === conversation.inbox_id)
            .map(item => item.id),
          label,
          inboxName: inbox.name,
          icon:
            channelType === INBOX_TYPES.TELEGRAM
              ? 'i-ri-telegram-fill'
              : 'i-ri-whatsapp-fill',
        };
      });

      return channels.map(channel => ({
        ...channel,
        label:
          channels.filter(item => item.label === channel.label).length > 1
            ? `${channel.label} · ${channel.inboxName}`
            : channel.label,
      }));
    },
    replyConversation() {
      if (this.currentChat?.unified_chat) {
        const selected = this.replyChannels.find(
          channel =>
            Number(channel.id) ===
            Number(
              this.replyConversationId ||
                this.currentChat.default_reply_conversation_id
            )
        );
        return {
          ...this.currentChat,
          inbox_id: selected?.inbox_id || this.currentChat.inbox_id,
          additional_attributes:
            selected?.additional_attributes ||
            this.currentChat.additional_attributes,
          reply_conversation_id: this.replyConversationId,
          selected_reply_conversation_id: selected?.id || null,
        };
      }
      const conversationId = Number(
        this.replyConversationId || this.currentChat?.id || 0
      );
      if (!conversationId) return this.currentChat;

      return (
        this.$store.getters.getConversationById(conversationId) ||
        this.omnichannelConversations.find(
          conversation => conversation.id === conversationId
        ) ||
        this.currentChat
      );
    },
    hasOmnichannelHistory() {
      if (this.currentChat?.unified_chat) return this.replyChannels.length > 1;
      return (
        new Set(
          this.omnichannelConversations.map(
            conversation => conversation.inbox_id
          )
        ).size > 1
      );
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.captainTasksEnabled &&
        this.isLabelSuggestionFeatureEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;
      if (this.isAnyoneTyping) {
        const [i18nKey, params] = getTypingUsersText(userList);
        return this.$t(i18nKey, params);
      }

      return '';
    },
    getMessages() {
      const messages = [
        ...(this.currentChat.messages || []),
        ...Object.values(this.relatedMessagesByConversation).flat(),
        ...this.omnichannelConversations.flatMap(conversation => {
          if (conversation.id === this.currentChat.id) return [];
          return (
            this.$store.getters.getConversationById(conversation.id)
              ?.messages || []
          );
        }),
      ];
      const uniqueMessages = [
        ...new Map(messages.map(message => [message.id, message])).values(),
      ].sort(
        (left, right) =>
          left.created_at - right.created_at || left.id - right.id
      );
      const decoratedMessages = this.hasOmnichannelHistory
        ? uniqueMessages.map(message => ({
            ...message,
            content_attributes: {
              ...message.content_attributes,
              omnichannel: true,
            },
          }))
        : uniqueMessages;

      return filterDuplicateSourceMessages(decoratedMessages);
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        this.isLoadingRelatedMessages ||
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },
    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (this.isATiktokChannel) {
        return REPLY_POLICY.TIKTOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (this.isATiktokChannel) {
        return this.$t('CONVERSATION.48_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count =
        this.unreadMessageCount > 9 ? '9+' : this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat?.id === oldChat?.id) {
        return;
      }
      if (newChat?.meta?.sender?.id !== oldChat?.meta?.sender?.id) {
        this.replyConversationId = null;
      }
      this.loadOmnichannelMessages();
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
      this.resetReplyEditorHeight();
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
      if (this.currentChat?.unified_chat) this.replyConversationId = null;
    });
  },

  mounted() {
    this.addScrollListener();
    this.loadOmnichannelMessages();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
  },

  unmounted() {
    this.removeBusListeners();
    this.removeScrollListener();
  },

  methods: {
    async fetchConversationHistory(
      conversationId,
      before,
      messages = [],
      page = 0
    ) {
      if (page >= MAX_HISTORY_PAGES) return messages;

      const response = await MessageApi.getPreviousMessages({
        conversationId,
        before,
      });
      const payload = response.data?.payload || [];
      if (!payload.length) return messages;

      const collectedMessages = [...payload, ...messages];
      if (payload.length < MESSAGE_PAGE_SIZE) return collectedMessages;

      return this.fetchConversationHistory(
        conversationId,
        payload[0].id,
        collectedMessages,
        page + 1
      );
    },
    async loadOmnichannelMessages() {
      if (this.currentChat?.unified_chat) {
        this.relatedMessagesByConversation = {};
        this.isLoadingRelatedMessages = false;
        return;
      }
      const contactId = this.contactId;
      const loadToken = this.omnichannelLoadToken + 1;
      this.omnichannelLoadToken = loadToken;
      this.relatedMessagesByConversation = {};

      if (!contactId) return;

      this.isLoadingRelatedMessages = true;
      try {
        await this.$store.dispatch('contactConversations/get', contactId);
        const conversations = this.omnichannelConversations.filter(
          conversation => conversation.id !== this.currentChat.id
        );
        const histories = await Promise.all(
          conversations.map(async conversation => {
            await this.$store.dispatch('getConversation', conversation.id);
            const messages = await this.fetchConversationHistory(
              conversation.id
            );
            return [conversation.id, messages];
          })
        );

        if (loadToken !== this.omnichannelLoadToken) return;

        this.relatedMessagesByConversation = Object.fromEntries(histories);
        this.selectDefaultReplyChannel();
      } catch (error) {
        if (loadToken === this.omnichannelLoadToken) {
          this.relatedMessagesByConversation = {};
        }
      } finally {
        if (loadToken === this.omnichannelLoadToken) {
          this.isLoadingRelatedMessages = false;
        }
      }
    },
    selectDefaultReplyChannel() {
      const availableConversationIds = new Set(
        this.replyChannels.map(channel => Number(channel.id))
      );
      if (availableConversationIds.has(Number(this.replyConversationId))) {
        return;
      }

      const latestIncoming = [...this.getMessages]
        .reverse()
        .find(
          message =>
            message.message_type === 0 &&
            this.replyChannels.some(channel =>
              (channel.conversationIds || [channel.id]).includes(
                Number(message.conversation_id)
              )
            )
        );
      const latestIncomingChannel = this.replyChannels.find(channel =>
        (channel.conversationIds || [channel.id]).includes(
          Number(latestIncoming?.conversation_id)
        )
      );
      const fallbackConversationId = availableConversationIds.has(
        Number(this.currentChat.id)
      )
        ? this.currentChat.id
        : this.replyChannels[0]?.id;

      this.replyConversationId =
        latestIncomingChannel?.id ||
        fallbackConversationId ||
        this.currentChat.id;
    },
    selectReplyChannel(conversationId) {
      const selectedConversationId = Number(
        conversationId?.id || conversationId
      );
      if (
        this.replyChannels.some(
          channel => Number(channel.id) === selectedConversationId
        )
      ) {
        this.replyConversationId = selectedConversationId;
      }
    },
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      // Early exit if conversation already has labels - no need to suggest more
      const existingLabels = this.currentChat?.labels || [];
      if (existingLabels.length > 0) return;

      if (!this.captainTasksEnabled || !this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.getLabelSuggestions();

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          messageElement.scrollIntoView({ behavior: 'smooth' });
          this.fetchPreviousMessages();
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.$nextTick(() => this.scrollToBottom());
      this.isLoadingPrevious = false;
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.isProgrammaticScroll = true;
      let relevantMessages = [];

      // label suggestions are not part of the messages list
      // so we need to handle them separately
      let labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else if (labelSuggestions) {
        // when scrolling to the bottom, the label suggestions is below the last message
        // so we scroll there if there are no unread messages
        // Unread messages always take the highest priority
        relevantMessages = [labelSuggestions];
      } else {
        // if there are no unread messages or label suggestion, scroll to the last message
        // capturing last message from the messages list
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      this.conversationPanel.scrollTop = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: this.currentChat.messages[0].id,
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
        this.hasUserScrolled = false;
      } else {
        this.hasUserScrolled = true;
      }
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    makeMessagesRead() {
      const conversationIds =
        this.hasOmnichannelHistory && !this.currentChat?.unified_chat
          ? this.omnichannelConversations.map(conversation => conversation.id)
          : [this.currentChat.id];
      conversationIds.forEach(id => {
        this.$store.dispatch('markMessagesRead', { id });
      });
    },
    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },
    toggleReplyEditorSize() {
      this.resizableEditorWrapperRef?.toggleEditorExpand?.();
    },
    resetReplyEditorHeight() {
      this.resizableEditorWrapperRef?.resetEditorHeight?.();
    },
  },
};
</script>

<template>
  <div
    ref="messagesViewRef"
    class="flex flex-col justify-between flex-grow h-full min-w-0 m-0"
  >
    <div ref="topBannerRef">
      <Banner
        v-if="!currentChat.can_reply"
        color-scheme="alert"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="replyWindowBannerMessage"
        :href-link="replyWindowLink"
        :href-link-text="replyWindowLinkText"
      />
      <Banner
        v-else-if="hasDuplicateInstagramInbox"
        color-scheme="alert"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
      />
    </div>
    <MessageList
      ref="conversationPanelRef"
      class="conversation-panel flex-shrink flex-grow basis-px flex flex-col overflow-y-auto relative h-full m-0 pb-4"
      :current-user-id="currentUserId"
      :first-unread-id="unReadMessages[0]?.id"
      :is-an-email-channel="isAnEmailChannel"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
      :messages="getMessages"
      @retry="handleMessageRetry"
    >
      <template #beforeAll>
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li
            class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
          >
            <Spinner v-if="shouldShowSpinner" class="text-n-brand" />
          </li>
        </transition>
      </template>
      <template #unreadBadge>
        <li
          v-show="unreadMessageCount != 0"
          class="list-none flex justify-center items-center"
        >
          <span
            class="shadow-lg rounded-full bg-n-brand text-white text-xs font-medium my-2.5 mx-auto px-2.5 py-1.5"
          >
            {{ unreadMessageLabel }}
          </span>
        </li>
      </template>
      <template #after>
        <ConversationLabelSuggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
      </template>
    </MessageList>
    <div class="flex relative flex-col bg-n-surface-1">
      <div
        v-if="isAnyoneTyping"
        class="absolute flex items-center w-full h-0 -top-7"
      >
        <div
          class="flex py-2 pr-4 pl-5 shadow-md rounded-full bg-white dark:bg-n-solid-3 text-n-slate-11 text-xs font-semibold my-2.5 mx-auto"
        >
          {{ typingUserNames }}
          <img
            class="w-6 ltr:ml-2 rtl:mr-2"
            src="assets/images/typing.gif"
            alt="Someone is typing"
          />
        </div>
      </div>
      <ResizableEditorWrapper
        ref="resizableEditorWrapperRef"
        :container-height="Math.max(0, containerHeight - topBannerHeight)"
      >
        <ReplyBox
          :conversation="replyConversation"
          :reply-channels="replyChannels"
          :active-reply-channel-id="
            currentChat.unified_chat
              ? replyConversation.selected_reply_conversation_id
              : replyConversation.id
          "
          @select-reply-channel="selectReplyChannel"
          @toggle-editor-size="toggleReplyEditorSize"
        />
      </ResizableEditorWrapper>
    </div>
  </div>
</template>
